import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urlBase64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - b64.length % 4) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
};

export const isPushSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/**
 * Register the Service Worker and subscribe to Web Push.
 * Returns a promise resolving to {state, permission}.
 *  - state: "subscribed" | "denied" | "unsupported" | "error"
 */
export const enableWebPush = async (authHeader) => {
  if (!isPushSupported()) return { state: "unsupported" };
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return { state: "denied", permission };
    const reg = await navigator.serviceWorker.register("/sw.js");
    // Wait for activation
    await navigator.serviceWorker.ready;

    // Get VAPID public key
    const pkResp = await axios.get(`${API}/push/public-key`);
    const publicKey = pkResp.data?.public_key;
    if (!publicKey) return { state: "error", reason: "no-public-key" };

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    await axios.post(`${API}/push/subscribe`, {
      endpoint: sub.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(sub.getKey("p256dh")),
        auth: arrayBufferToBase64(sub.getKey("auth")),
      },
      user_agent: navigator.userAgent,
    }, { headers: authHeader() });
    return { state: "subscribed" };
  } catch (e) {
    return { state: "error", reason: e?.message || "unknown" };
  }
};

export const disableWebPush = async (authHeader) => {
  if (!isPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && await reg.pushManager.getSubscription();
    if (sub) {
      await axios.post(`${API}/push/unsubscribe`, {
        endpoint: sub.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(sub.getKey("p256dh")),
          auth: arrayBufferToBase64(sub.getKey("auth")),
        },
      }, { headers: authHeader() });
      await sub.unsubscribe();
    }
  } catch { /* ignore */ }
};

const arrayBufferToBase64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
};

export const currentPushPermission = () => {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // default | granted | denied
};
